import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Account } from '@shared/models/account';
import { Activity, ResourceEntry } from '@shared/models/models';
import { UploadFileResponse } from '@shared/models/image';
import { Resource } from '@shared/models/resource';
import { ResourceAccount } from '@shared/models/resourceAccount';
import { environment } from '@environments/environment';
import { LockerRoomResponse } from '@app/features/locker-rooms';

// Endpoint root
const APIURL = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class ResourceService {
  private readonly http = inject(HttpClient);
  getList(locationId: number | null, expand: boolean): Observable<Resource[]> {
    let params = '';

    if (locationId) {
      params += `?location_id=${locationId}`;
      if (expand) {
        params += '&expand=location,parent,galleries';
      }
    }

    return this.http.get<Resource[]>(`${APIURL}/resources/${params}`);
  }
  create(payload: Resource): Observable<Resource> {
    return this.http.post<Resource>(`${APIURL}/resources/`, payload);
  }
  get(id: number, expand: boolean): Observable<Resource> {
    let params = '';
    if (expand) {
      params += '?expand=location,parent,activities,dependencies,galleries';
    }
    return this.http.get<Resource>(`${APIURL}/resources/${id}/${params}`);
  }
  getEntries(id: number): Observable<ResourceEntry[]> {
    return this.http.get<ResourceEntry[]>(`${APIURL}/resources/${id}/entries`);
  }
  getActivities(id: number): Observable<Activity[]> {
    return this.http.get<Activity[]>(`${APIURL}/resources/${id}/activities`);
  }
  getChildren(id: number): Observable<Resource[]> {
    return this.http.get<Resource[]>(`${APIURL}/resources/${id}/children`);
  }
  update(payload: Resource): Observable<Resource> {
    return this.http.put<Resource>(`${APIURL}/resources/${payload.id}`, payload);
  }
  delete(id: number): Observable<Resource> {
    return this.http.delete<Resource>(`${APIURL}/resources/${id}`);
  }

  getAccount(id: number): Observable<ResourceAccount> {
    return this.http.get<ResourceAccount>(`${APIURL}/resources/${id}/accounts`);
  }

  getAccounts(locationId: number): Observable<Account[]> {
    return this.http.get<Account[]>(`${APIURL}/resources/accounts?locationId=${locationId}`);
  }

  toTreeview(resources: Resource[]): Resource[] {
    const mapTree: Record<number, number> = {};
    const roots: Resource[] = [];
    let node: Resource = {} as Resource;
    let i = 0;

    for (i = 0; i < resources.length; i += 1) {
      mapTree[resources[i].id] = i; // initialize the map
      resources[i].children = []; // initialize the children
    }

    for (i = 0; i < resources.length; i += 1) {
      node = resources[i];

      if (!node.isActive) {
        node.name = `${node.name} (inaktiv)`;
      }

      if (node.parentId > 0) {
        // if you have dangling branches check that map[node.parentId] exists
        resources[mapTree[node.parentId]]?.children.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }

  getBySlug(slug: string): Observable<{ id: number } | null> {
    return this.http
      .get<{ id: number }>(`${APIURL}/resources/slug/${encodeURIComponent(slug)}`)
      .pipe(catchError(() => of(null)));
  }

  isSlugAvailable(slug: string, excludeId: number | null): Observable<boolean> {
    const params = excludeId != null ? `?excludeId=${excludeId}` : '';
    return this.http
      .get<boolean>(`${APIURL}/resources/slug/${encodeURIComponent(slug)}/available${params}`)
      .pipe(catchError(() => of(true)));
  }

  uploadFile(payload: FormData): Observable<UploadFileResponse> {
    return this.http.post<UploadFileResponse>(`${APIURL}/upload`, payload);
  }

  getRelatedLockerRooms(resourceId: number): Observable<LockerRoomResponse[]> {
    return this.http.get<LockerRoomResponse[]>(`${APIURL}/resources/${resourceId}/locker-rooms`);
  }
}
